import {gettextCatalog} from "angular-gettext";
import _ from "lodash";
import moment from "moment";

export enum Ukedager {mandag = 1, tirsdag = 2, onsdag = 3, torsdag = 4, fredag = 5, lørdag = 6, søndag = 0}

export enum Måneder {januar = 0, februar, mars, april, mai, juni, juli, august, september, oktober, november, desember}

export var millisITime = 60 * 60 * 1000;

/**
 * Returnerer en array av arrayer bestående av Date objekter, en ytre array per uke i forespurt intervall,
 * den indre arrayen inneholder ett innslag per ukedag i uken, med mandag som første innslag.
 */
export function heleUkerIMåned(år: number, måned: number): Date[][] {
    var uker: Date[][] = [];

    var start = mandagIUkenSomInneholder(new Date(år, måned, 1));
    var slutt = søndagIUkenSomInneholder(new Date(år, måned + 1, 0)); //date=0 gir siste dag i foregående måned

    var uke = 0;
    do {
        var dag = new Date(start.getTime());
        if (uker.length <= uke) uker.push([]);
        uker[uke].push(dag);
        if (dag.getDay() == Ukedager.søndag) uke++;
        start.setDate(start.getDate() + 1);
    } while (start.getTime() <= slutt.getTime());

    return uker;
}

export function erIdag(dato: Date): boolean {
    return erSammeDag(dato, new Date());
}

export function mandagIUkenSomInneholder(dato: Date): Date {
    var dagerSidenMandag = Ukedager.mandag - (dato.getDay() == Ukedager.søndag ? 7 : dato.getDay());
    var mandag = new Date(dato.getTime());
    mandag.setDate(mandag.getDate() + dagerSidenMandag);
    return mandag;
}

export function søndagIUkenSomInneholder(dato: Date): Date {
    var dagerTilSøndag = 7 - (dato.getDay() == Ukedager.søndag ? 7 : dato.getDay());
    var søndag = new Date(dato.getTime());
    søndag.setDate(søndag.getDate() + dagerTilSøndag);
    return søndag;
}

export function somKl0000(dato: Date): Date {
    return new Date(dato.getFullYear(), dato.getMonth(), dato.getDate(), 0, 0, 0, 0);
}

export function somKl2359(dato: Date): Date {
    return new Date(dato.getFullYear(), dato.getMonth(), dato.getDate(), 23, 59, 59, 999);
}

export function medKlokkeslett(dato: Date, timer: number, minutter: number): Date {
    return new Date(dato.getFullYear(), dato.getMonth(), dato.getDate(), timer, minutter, 0, 0);
}

export function iDagKl00(): Date {
    return somKl0000(new Date());
}

export function erSammeDag(a: Date, b: Date): boolean {
    return a.getDate() === b.getDate()
        && a.getMonth() === b.getMonth()
        && a.getFullYear() === b.getFullYear()
}

export function erSammeMåned(a: Date, b:Date): boolean {
    return a.getMonth() === b.getMonth()
    && a.getFullYear() === b.getFullYear();
}

export function erPeriodenPåSammeDag(periode: { start: Date; slutt: Date; }, dato: Date) {
    return moment(dato).isBetween(periode.start, periode.slutt, "day", "[]");
}

export function erIPerioden(periode: { start: Date; slutt: Date; }, dato: Date): boolean {
    if (periode.start === null) return periode.slutt.getTime() >= dato.getTime();
    if (periode.slutt === null) return periode.start.getTime() <= dato.getTime();
    return periode.start.getTime() <= dato.getTime() && periode.slutt.getTime() >= dato.getTime()
}

export function tidligsteAv(...datoer: Date[]): Date {
    return datoer.sort((a, b) => a.getTime() - b.getTime())[0];
}

export function senesteAv(...datoer: Date[]): Date {
    return datoer.sort((a, b) => b.getTime() - a.getTime())[0];
}

export function klokkeslett5MinIntervaller(): string[] {
    return _(new Array(24))
      .map((verdi, time) =>
        _(new Array(12))
          .map((verdi, minutt) => `${_.padStart(`${time}`, 2, '0')}:${_.padStart(`${minutt * 5}`, 2, '0')}`)
          .value())
      .flatten()
      .value();
}

export function varigheter5MinIntervaller(gettextCatalog: gettextCatalog): VarigheterInnslag[] {
    let varigheter = _(new Array(24 * 12)) //en time innholder 12 fem-minutters-intervaller, ett døgn inneholder 24 timer
      .map((verdi, teller) => {
          const minutter = teller * 5;
          return {navn: formaterVartighet(minutter, gettextCatalog), verdi: minutter};
      })
      .drop(1) //fjerner første innslag som repr 0 minutter
      .value();
    //legg til 1min og 2min som tilgjengelige varigheter før 5min
    return [
        {navn: formaterVartighet(1, gettextCatalog), verdi: 1},
        {navn: formaterVartighet(2, gettextCatalog), verdi: 2},
        ...varigheter
    ];
}

export function varigheterØkendeIntervaller(gettextCatalog: gettextCatalog): VarigheterInnslag[] {
    const minutter = [15, 30];
    for (let i = 60; i <= 480; i += 30) minutter.push(i); //halvtimer opp til 8 timer

    return _.map(minutter, minutter => {
        return {navn: formaterVartighet(minutter, gettextCatalog), verdi: minutter}
    });
}

export function formaterVartighet(minutter: number, gettextCatalog: gettextCatalog) {
    const antallMinutter = minutter % 60;
    const antallTimer = Math.floor(minutter / 60);
    const minutterSomStreng = antallTimer > 0 ? _.padStart(Number(antallMinutter).toString(), 2, '0') : Number(antallMinutter).toString();
    const kontekst = {timer: antallTimer, minutter: minutterSomStreng};

    if (antallTimer == 0)
    /// formatering i varighet-nedtrekksmeny - under 1 time
        return gettextCatalog.getPlural(antallMinutter, '{{minutter}} min', '{{minutter}} min', kontekst);
    if (antallMinutter == 0)
    /// formatering i varighet-nedtrekksmeny - hele timer
        return gettextCatalog.getPlural(antallTimer, '{{timer}} time', '{{timer}} timer', kontekst);
    /// formatering i varighet-nedtrekksmeny - timer og minutter
    return gettextCatalog.getPlural(antallTimer, '{{timer}}:{{minutter}} time', '{{timer}}:{{minutter}} timer', kontekst);
}

export interface VarigheterInnslag {
    navn: string;
    verdi: number;
}
